"use client";

import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { convertDate, getAddressAttributes } from "@/lib/utils";
import { useCompanyAddresses, useCompanyDetails } from "@/queries/company";
import { useCheckoutStore } from "@/stores/checkout-store";
import { DeliveryAddress } from "@/types";
import { Loader2 } from "lucide-react";

type DeliveryRecapProps = {
  locale: Locales;
};

const DeliveryRecap = ({ locale }: DeliveryRecapProps) => {
  const { customer } = useCustomer();
  const phone = useCheckoutStore((state) => state.phone);
  const title = useCheckoutStore((state) => state.title);
  const titleLabel = title === "mademoiselle" ? __("Mrs.", locale) : title === "madame" ? __("Miss", locale) : "Mr";
  const firstName = useCheckoutStore((state) => state.firstName);
  const lastName = useCheckoutStore((state) => state.lastName);
  const deliveryDate = useCheckoutStore((state) => state.deliveryDate);
  const recoveryDate = useCheckoutStore((state) => state.recoveryDate);
  const accountType = useCheckoutStore((state) => state.accountType);

  const deliveryAddressId = useCheckoutStore((state) => state.deliveryAddress);
  const isCompanyAccountSelected = accountType === "company";
  const { data: companyDetails, isLoading: isCompanyLoading } = useCompanyDetails(
    customer?.company_name || "",
    customer?.id || "anonymous"
  );
  const { data: companyAddresses, isLoading: isCompanyAddressesLoading } = useCompanyAddresses(
    isCompanyAccountSelected ? companyDetails?.id : ""
  );
  const deliveryAddresses: DeliveryAddress[] | undefined = isCompanyAccountSelected
    ? companyAddresses
    : customer?.addresses;
  const { deliveryAddress } = getAddressAttributes(deliveryAddresses, undefined, deliveryAddressId);
  const isLoading =
    customer === undefined || (isCompanyAccountSelected && (isCompanyLoading || isCompanyAddressesLoading));

  return (
    <section className="space-y-5">
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Returns", locale)}</h2>
      <div className="border-primary-light/30 space-y-4 rounded-md border p-4">
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <dl className="grid w-full gap-2.5 md:grid-cols-2 md:gap-11.5">
            <div className="space-y-2.5">
              {titleLabel && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Civility", locale)}</dt>
                  <dd className="font-semibold">{titleLabel}</dd>
                </div>
              )}
              {firstName && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("First Name", locale)} </dt>
                  <dd className="font-semibold">{firstName}</dd>
                </div>
              )}
              {lastName && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Last Name", locale)} </dt>
                  <dd className="font-semibold">{lastName}</dd>
                </div>
              )}
              {deliveryDate && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Delivery Date", locale)} </dt>
                  <dd className="font-semibold capitalize">{convertDate(deliveryDate, "dd MMM yyyy", locale)}</dd>
                </div>
              )}
              {recoveryDate && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Recovery Date", locale)}</dt>
                  <dd className="font-semibold capitalize">{convertDate(recoveryDate, "dd MMM yyyy", locale)}</dd>
                </div>
              )}
            </div>
            <div className="space-y-2.5">
              {phone && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Phone", locale)}</dt>
                  <dd className="font-semibold">{phone}</dd>
                </div>
              )}
              {deliveryAddress?.address_1 && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("Shipping address", locale)}</dt>
                  <dd className="font-semibold">{deliveryAddress?.address_1}</dd>
                </div>
              )}
              {deliveryAddress?.postal_code && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("ZIP Code", locale)}</dt>
                  <dd className="font-semibold">{deliveryAddress?.postal_code}</dd>
                </div>
              )}
              {deliveryAddress?.city && (
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <dt>{__("City", locale)}</dt>
                  <dd className="font-semibold">{deliveryAddress?.city}</dd>
                </div>
              )}
            </div>
          </dl>
        )}
      </div>
    </section>
  );
};

export default DeliveryRecap;
